// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Support Staff Admin API Client
 */

import type {
  SupportStaff,
  SupportStaffInvite,
  SupportStaffUpdate,
  SupportStaffDetail,
  AdvisorForAssignment,
  BulkAssignRequest,
  BulkAssignResult,
  SupportActivityLog,
  AssignmentHistoryItem,
  PageResponse,
} from '../types/supportStaff';

const API_BASE = '/api/admin/support-staff';

// Response type for the /assignment-history endpoint
interface AssignmentHistoryResponse {
  id: string;
  assignmentType: 'ADVISOR' | 'PROFILE';
  status: 'ACTIVE' | 'INACTIVE';
  assignedAt: string;
  assignedBy: {
    id: string | null;
    name: string;
    email: string | null;
  };
  unassignedAt: string | null;
  unassignedBy: {
    id: string | null;
    name: string;
    email: string | null;
  } | null;
  target: {
    id: string;
    name: string;
    email: string | null;
  };
}

// Response type for the /assignments endpoint
interface AdvisorAssignmentsResponse {
  supportAdvisorId: string;
  supportAdvisorName: string;
  supportAdvisorEmail: string;
  assignments: {
    id: string;
    advisorId: string;
    advisorName: string;
    advisorEmail: string;
    profileCount: number;
    pendingLOACount: number;
    status: string;
    assignedAt: string;
    assignedByName: string;
  }[];
  totalAssignments: number;
  totalProfiles: number;
  totalPendingLOAs: number;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }
  return response.json();
}

async function handleVoidResponse(response: Response): Promise<void> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }
}

export const supportStaffApi = {
  /**
   * Get all support staff with pagination and filters
   */
  getAll: async (
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction = 'DESC',
    status?: string,
    brokerageId?: string,
    search?: string
  ): Promise<PageResponse<SupportStaff>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort,
      direction,
    });
    if (status && status !== 'all') {
      params.append('status', status);
    }
    if (brokerageId && brokerageId !== 'all') {
      params.append('brokerageId', brokerageId);
    }
    if (search) {
      params.append('search', search);
    }
    const response = await fetch(`${API_BASE}?${params}`);
    return handleResponse<PageResponse<SupportStaff>>(response);
  },

  /**
   * Get support staff by ID with full details
   */
  getById: async (id: string): Promise<SupportStaffDetail> => {
    const response = await fetch(`${API_BASE}/${id}`);
    return handleResponse<SupportStaffDetail>(response);
  },

  /**
   * Invite new support staff
   */
  invite: async (invite: SupportStaffInvite): Promise<SupportStaff> => {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invite),
    });
    return handleResponse<SupportStaff>(response);
  },

  /**
   * Update support staff details
   */
  update: async (id: string, update: SupportStaffUpdate): Promise<SupportStaff> => {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(update),
    });
    return handleResponse<SupportStaff>(response);
  },

  /**
   * Deactivate support staff
   */
  deactivate: async (id: string, reason?: string): Promise<SupportStaff> => {
    const response = await fetch(`${API_BASE}/${id}/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
    });
    return handleResponse<SupportStaff>(response);
  },

  /**
   * Reactivate support staff
   */
  reactivate: async (id: string): Promise<SupportStaff> => {
    const response = await fetch(`${API_BASE}/${id}/reactivate`, {
      method: 'POST',
    });
    return handleResponse<SupportStaff>(response);
  },

  /**
   * Delete support staff
   */
  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
    });
    return handleVoidResponse(response);
  },

  /**
   * Get advisors available for assignment
   */
  getAvailableAdvisors: async (
    supportStaffId: string,
    brokerageId: string
  ): Promise<AdvisorForAssignment[]> => {
    const params = new URLSearchParams({ brokerageId });
    const response = await fetch(
      `${API_BASE}/${supportStaffId}/available-advisors?${params}`
    );
    return handleResponse<AdvisorForAssignment[]>(response);
  },

  /**
   * Get assigned advisors for support staff
   */
  getAssignedAdvisors: async (supportStaffId: string): Promise<AdvisorForAssignment[]> => {
    const response = await fetch(`${API_BASE}/${supportStaffId}/assignments`);
    const data = await handleResponse<AdvisorAssignmentsResponse>(response);

    // Transform assignments to AdvisorForAssignment format
    return data.assignments.map((assignment) => {
      // Split advisorName into firstName and lastName
      const nameParts = assignment.advisorName?.split(' ') || ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      return {
        id: assignment.advisorId,
        firstName,
        lastName,
        email: assignment.advisorEmail,
        profileCount: assignment.profileCount || 0,
        isAssigned: true, // These are already assigned advisors
      };
    });
  },

  /**
   * Assign advisors to support staff
   */
  assignAdvisors: async (
    supportStaffId: string,
    brokerageId: string,
    request: BulkAssignRequest
  ): Promise<BulkAssignResult> => {
    const response = await fetch(`${API_BASE}/${supportStaffId}/assignments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Brokerage-Id': brokerageId,
      },
      body: JSON.stringify(request),
    });
    return handleResponse<BulkAssignResult>(response);
  },

  /**
   * Unassign advisor from support staff
   */
  unassignAdvisor: async (
    supportStaffId: string,
    brokerageId: string,
    advisorId: string
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE}/${supportStaffId}/assignments/${advisorId}`,
      {
        method: 'DELETE',
        headers: {
          'X-Brokerage-Id': brokerageId,
        },
      }
    );
    return handleVoidResponse(response);
  },

  /**
   * Get activity log for support staff
   */
  getActivityLog: async (
    supportStaffId: string,
    page = 0,
    size = 20
  ): Promise<PageResponse<SupportActivityLog>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    const response = await fetch(`${API_BASE}/${supportStaffId}/activity?${params}`);
    return handleResponse<PageResponse<SupportActivityLog>>(response);
  },

  /**
   * Get assignment history for support staff
   */
  getAssignmentHistory: async (
    supportStaffId: string,
    page = 0,
    size = 20
  ): Promise<PageResponse<AssignmentHistoryItem>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    const response = await fetch(
      `${API_BASE}/${supportStaffId}/assignment-history?${params}`
    );
    const data = await handleResponse<PageResponse<AssignmentHistoryResponse>>(response);

    // Transform API response to UI expected format
    const transformedContent: AssignmentHistoryItem[] = data.content.map((item) => ({
      id: item.id,
      type: item.status === 'ACTIVE' ? 'ASSIGNED' : 'UNASSIGNED',
      advisorId: item.target.id,
      advisorName: item.target.name,
      performedById: item.status === 'ACTIVE'
        ? (item.assignedBy?.id || '')
        : (item.unassignedBy?.id || ''),
      performedByName: item.status === 'ACTIVE'
        ? (item.assignedBy?.name || 'Unknown')
        : (item.unassignedBy?.name || 'Unknown'),
      timestamp: item.status === 'ACTIVE' ? item.assignedAt : (item.unassignedAt || item.assignedAt),
    }));

    return {
      ...data,
      content: transformedContent,
    };
  },

  /**
   * Resend invitation to support staff
   */
  resendInvitation: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/${id}/resend-invitation`, {
      method: 'POST',
    });
    return handleVoidResponse(response);
  },
};
